import * as React from "react";
import "./styles.scss";
import {
	ProcessStage,
	ProcessStageArrow,
	ProcessStageArrowDirection,
	type ProcessStageOrder,
	useProcessController,
} from "../../../../Processes";
import {VeBoxCard} from "../../../../../components/card/VeBoxCard";
import {Display} from "../../../../../components/Display";
import {motion, type MotionProps} from "framer-motion";
import {CensusCoolerContext} from "../../providers/CensusCollerProvider";
import {EmptyState, infoIcons} from "@vebox/etui-core";
import {Loader} from "../../../../../components/Utils/loader/Loader";
import {createData, createFailPieCharData, createOkPieCharData, createPieCharData, getPieChartLegend,} from "./utils";
import {SummaryCardFooter} from "../../components/SummaryCardFooter";
import {SuggestedActionsContext} from "../../providers/SuggestedActionsProvider";
import {RestDataLoader} from "../../../../../dashboard/widgets/unilever/RestDataLoader";
import {CensusStatusPieChart} from "../../widgets/CensusPieChart";
import {useCensusEnums} from "../../data/hooks/CecnsusCoolerControllerHooks";

const DATA_PER_CHART = 5;

interface CensusSummaryPageProps {
    stepNumber: number;
    stageOrder: ProcessStageOrder;
    title: string;
    divProps: MotionProps;
}

export const CensusSummaryPage: React.FC<CensusSummaryPageProps> = ({
                                                                        stepNumber,
                                                                        stageOrder,
                                                                        title,
                                                                        divProps,
                                                                    }) => {
    const censusEnums = useCensusEnums(true);
    const processController = useProcessController();
    const {AppTranslations, census, fromCensusHistory} =
        React.useContext(CensusCoolerContext);
    const {isLoadingActions, suggestedActionsAnalysisSummary} =
        React.useContext(SuggestedActionsContext);

    const [isEmpty, setIsEmpty] = React.useState(false);
    const [isExporting, setIsExporting] = React.useState(false);

    const startExport = () => {
        if (!isExporting) {
            setIsExporting(true);
            RestDataLoader.downloadOctetStream(
                `census/${census?.guid}/entries/export`,
                {},
                () => setIsExporting(false),
                () => {
                    console.error();
                    setIsExporting(false);
                },
            );
        }
    };

    const goBackHome = () => {
        if (fromCensusHistory) {
            history.back();
        } else {
            window.location.reload();
        }
    };

    const chartsData = React.useMemo(() => {
        if (
            !isLoadingActions &&
            suggestedActionsAnalysisSummary &&
            !censusEnums?.isLoading
        ) {
            return {
                generalData: createPieCharData(
                    suggestedActionsAnalysisSummary,
                    censusEnums?.data?.censusEquipmentStatuses,
                ),
                failData: createFailPieCharData(
                    suggestedActionsAnalysisSummary,
                    DATA_PER_CHART,
                    censusEnums?.data,
                ),
                okData: createOkPieCharData(
                    suggestedActionsAnalysisSummary,
                    DATA_PER_CHART,
                    censusEnums?.data,
                ),
            };
        }
        return {
            generalData: [],
            failData: {data: [], otherData: []},
            okData: {data: [], otherData: []},
        };
    }, [
        suggestedActionsAnalysisSummary,
        isLoadingActions,
        censusEnums?.isLoading,
    ]);

    const legendItems = React.useMemo(() => {
        if (
            suggestedActionsAnalysisSummary?.summary &&
            suggestedActionsAnalysisSummary?.summary.length > 0
        ) {
            setIsEmpty(false);
            return getPieChartLegend({
                items: chartsData.generalData,
                total: chartsData.generalData.reduce(
                    (acc, item) => acc + item.frequency,
                    0,
                ),
            });
        } else {
            setIsEmpty(true);
            return [];
        }
    }, [chartsData.generalData]);

    const legendFailItems = React.useMemo(() => {
        if (
            suggestedActionsAnalysisSummary?.summary &&
            suggestedActionsAnalysisSummary?.summary.length > 0
        ) {
            setIsEmpty(false);
            return {
                ...createData({
                    data: chartsData.failData.data,
                    otherData: chartsData.failData.otherData,
                }),
            };
        } else {
            setIsEmpty(true);
            return {data: [], otherData: []};
        }
    }, [chartsData.failData]);

    const legendOkItems = React.useMemo(() => {
        if (
            suggestedActionsAnalysisSummary?.summary &&
            suggestedActionsAnalysisSummary?.summary.length > 0
        ) {
            setIsEmpty(false);
            return {
                ...createData({
                    data: chartsData.okData.data,
                    otherData: chartsData.okData.otherData,
                }),
            };
        } else {
            setIsEmpty(true);
            return {data: [], otherData: []};
        }
    }, [chartsData.okData]);
    return (
        <Display isVisible={processController.position + 1 === stepNumber}>
            <motion.div key={stepNumber} {...divProps}>
                <ProcessStage
                    stepNumber={stepNumber}
                    title={title}
                    stageOrder={stageOrder}
                >
                    <div className={"flx-row"}>
                        <ProcessStageArrow
                            stepNumber={stepNumber}
                            direction={ProcessStageArrowDirection.BACKWARD}
                            visible={false}
                        />
                        <VeBoxCard className={"census-summary-container"}>
                            <Display isVisible={isLoadingActions}>
                                <Loader/>
                            </Display>
                            <Display isVisible={!isEmpty && !isLoadingActions}>
                                <div className="widget-content">
                                    <div className={"left-widget-content"}>
                                        <CensusStatusPieChart
                                            data={chartsData.generalData}
                                            legendData={legendItems}
                                            cartLegendClassName={"chart-legend"}
                                            chartClassName={"general-chart"}
                                            noDataMessage={"0"}
                                            title={
                                                AppTranslations.massiveActions.censusCooler
                                                    .censusResultTitle
                                            }
                                        />
                                    </div>
                                    <div className={"top-widget-content"}>
                                        <CensusStatusPieChart
                                            data={chartsData.failData.data}
                                            legendData={legendFailItems?.data}
                                            otherLegendData={legendFailItems?.otherData}
                                            cartLegendClassName={"fail-chart-legend"}
                                            chartClassName={"fail-chart"}
                                            noDataMessage={"0"}
                                            title={
                                                AppTranslations.massiveActions.censusCooler
                                                    .censusResultTitleFailDistribution
                                            }
                                        />
                                    </div>
                                    <div className={"bottom-widget-content"}>
                                        <CensusStatusPieChart
                                            data={chartsData.okData?.data}
                                            legendData={legendOkItems?.data}
                                            otherLegendData={legendOkItems?.otherData}
                                            cartLegendClassName={"ok-chart-legend"}
                                            chartClassName={"ok-chart"}
                                            noDataMessage={"0"}
                                            title={
                                                AppTranslations.massiveActions.censusCooler
                                                    .censusResultTitleOkDistribution
                                            }
                                        />
                                    </div>
                                </div>
                                <SummaryCardFooter
                                    onExport={startExport}
                                    onNext={goBackHome}
                                    loadingExport={isExporting}
                                    disabled={isLoadingActions || isExporting}
                                    disableExport={isLoadingActions}
                                />
                            </Display>
                            <Display isVisible={isEmpty && !isLoadingActions}>
                                <div className={"vertical-align-center"}>
                                    <EmptyState>
                                        <EmptyState.InfoIcon
                                            styles={{
                                                root: {
                                                    color: "var(--empty-indicator-svg-stroke)",
                                                },
                                            }}
                                            glyph={infoIcons.NoContent}
                                        />
                                        <EmptyState.Title color="var(--empty-indicator-title-color)">
                                            {AppTranslations.noDataAvailable}
                                        </EmptyState.Title>
                                    </EmptyState>
                                </div>
                            </Display>
                        </VeBoxCard>
                        <ProcessStageArrow
                            stepNumber={stepNumber}
                            direction={ProcessStageArrowDirection.FORWARD}
                            visible={false}
                        />
                    </div>
                </ProcessStage>
            </motion.div>
        </Display>
    );
};
